import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    type MetricChartSpec,
    type UseMetricChartResult,
    useMetricChart,
} from '@/components/charts/generic';
import { graphSettings } from '@/config/settings';
import type { WelfareData } from '@/domain/welfare/types';
import {
    HEALTH_LINE_TENSION,
    HEALTH_POINT_MARKER,
    healthPercentAxis,
    healthTooltipTitle,
    percentPoints,
} from '@/routes/Client/Cage/pages/Health/_utils/healthChartSpec';
import { type Locale, parseLocale } from '@/utils/localeFormatters';

const SKIN_SPECKLES_COLOR = graphSettings.colors.primary;
const TREND_LINE_COLOR = graphSettings.daytagColors.trend_line;

export interface ScaleLossChartLabels {
    skinSpeckles: string;
    trend: string;
}

/**
 * Scale loss (skin speckles) per day in percent, with its fitted trend: both toggleable, the
 * trend kept out of the CSV download.
 */
export function buildScaleLossSpec(
    fishHealthData: WelfareData,
    labels: ScaleLossChartLabels,
    locale: Locale
): MetricChartSpec<'line'> {
    const { days, trend } = fishHealthData;
    return {
        kind: 'line',
        days: days.map((day) => day.day),
        series: [
            {
                key: 'skinSpeckles',
                label: labels.skinSpeckles,
                color: SKIN_SPECKLES_COLOR,
                tension: HEALTH_LINE_TENSION,
                marker: HEALTH_POINT_MARKER,
                data: percentPoints(days, (day) => day.skinSpeckles),
            },
            {
                key: 'trend',
                label: labels.trend,
                color: TREND_LINE_COLOR,
                tension: HEALTH_LINE_TENSION,
                marker: HEALTH_POINT_MARKER,
                excludeFromCsv: true,
                data: percentPoints(trend, (point) => point.skinSpeckles),
            },
        ],
        left: healthPercentAxis(labels.skinSpeckles),
        locale,
        tooltipTitle: healthTooltipTitle(locale),
        tooltipSkipEmpty: true,
    };
}

export function useScaleLossChart(fishHealthData: WelfareData): UseMetricChartResult<'line'> {
    const { t, i18n } = useTranslation();
    const spec = useMemo(
        () =>
            buildScaleLossSpec(
                fishHealthData,
                {
                    skinSpeckles: t('fishHealthIndicators.skinSpeckles'),
                    trend: t('daypartTag.trend_line'),
                },
                parseLocale(i18n.language)
            ),
        [fishHealthData, t, i18n.language]
    );
    return useMetricChart(spec);
}
